import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

export default createVuetify({
  components,
  directives,
  // 🔴 V2's `item-text` defaulted to `text`; V3's `item-title` defaults to `title`. Every
  // item array in this app is `{ text, value }`, so without this the option labels on 56
  // selects render blank — the dropdown opens and the rows are empty. Restoring the default
  // globally is one line against 56 call sites, and it is V2's behaviour exactly rather
  // than a new convention. `item-value` is unaffected: both versions default to `value`.
  // Plain string arrays are unaffected too — V3 uses primitives directly.
  // 🔴 The defaults above are global, so they also reach the VSelects Vuetify renders for
  // ITSELF. VDataTableFooter builds its items-per-page select from `[{ value: 10, title: '10' },
  // …]` and passes no itemTitle of its own (VDataTableFooter.js:55, :113) — so `itemTitle: 'text'`
  // looks for a key that is not there, getPropertyFromItem falls back to the raw item, and the
  // field renders "[object Object]". The same object reaches VListItem's `title` prop when the
  // menu opens, which is typed [String, Number, Boolean] and warns.
  // Nested defaults scope by ancestor, so the override is aimed at VDataTableFooter — the one
  // component that actually renders a `title`-keyed select (it builds and passes it itself,
  // VDataTableFooter.js:113). It was previously aimed at VDataTable, which is every select the APP
  // renders anywhere inside a table too: CockUpSettings' Department column and MaterialOrderView's
  // sheet-size override both pass {text, value} and both came out as "[object Object]", the very
  // symptom this entry exists to prevent.
  //
  // Naming the footer is also what makes it hold for VDataTableServer and VDataTableVirtual, which
  // render the same footer — the old VDataTable key would have needed an entry each. Every Vuetify
  // component feeds its own name to internalUseDefaults and provides capitalised sub-entries to its
  // descendants (util/defineComponent.js:32, composables/defaults.js:76-91), so a key is reachable
  // for any ancestor, not just a table root.
  // 🔴 V3's VField — which backs text-field, select, autocomplete, combobox, textarea and
  // file-input — defaults to `variant: 'filled'` (VField.js:51-55). V2's default input was the
  // plain UNDERLINED one; `filled`/`solo`/`outlined` were all opt-in there. So every input that
  // never named a variant silently became a grey filled box: 241 of them — 170 text-fields, 46
  // selects, 23 autocompletes, 2 textareas — against 26 that do name one. On a form-heavy app
  // that is the single largest visual difference from V2, and no lint rule can see it because
  // nothing is deprecated; the default simply changed underneath.
  //
  // Set here rather than at 241 call sites, for the same reason as itemTitle above. The 26
  // explicit variants still win — a prop always beats a default — and so does Vuetify's own
  // footer select, which passes variant="outlined" itself (VDataTableFooter.js:113).
  defaults: {
    VTextField: { variant: 'underlined' },
    VTextarea: { variant: 'underlined' },
    VFileInput: { variant: 'underlined' },
    VSwitch: { color: 'primary' },
    VSelect: { itemTitle: 'text', variant: 'underlined' },
    VAutocomplete: { itemTitle: 'text', variant: 'underlined' },
    VCombobox: { itemTitle: 'text', variant: 'underlined' },
    VDataTableFooter: { VSelect: { itemTitle: 'title' } }
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi }
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          primary: '#1B3246',
          secondary: '#F7F4EB',
          accent: '#FF847F',
          error: '#FF5252',
          info: '#2196F3',
          success: '#4CAF50',
          warning: '#ed8600'
        }
      },
      dark: {
        dark: true,
        colors: {
          primary: '#1B3246',
          secondary: '#F7F4EB',
          accent: '#FF847F',
          error: '#FF5252',
          info: '#2196F3',
          success: '#4CAF50',
          warning: '#ed8600'
        }
      }
    }
  }
})
